// The docked panel takes this width when there is room for it beside the page, until
// the member resizes it.
export const DOCKED_PANEL_WIDTH = 420;

// The narrowest the panel gets, docked or floating.
export const MIN_PANEL_WIDTH = 320;

export const MIN_PAGE_WIDTH_BESIDE_DOCKED_PANEL = 762;

// A secondary panel takes at most 35% of the window (spacing.md 4.11).
export const MAX_DOCKED_PANEL_PERCENT = 35;

export type DockedPanelWidthLimits = { min: number; max: number };

// The docked panel is never narrower than its minimum, and never wider than 35% of the
// window or the room that leaves the page its minimum width. Below the panel's minimum
// there is no room to dock, and this is null.
export const dockedPanelWidthLimits = (
  widthBesideScrollbar: number,
  sidebarWidth: number,
): DockedPanelWidthLimits | null => {
  const room = widthBesideScrollbar - sidebarWidth - MIN_PAGE_WIDTH_BESIDE_DOCKED_PANEL;
  if (room < MIN_PANEL_WIDTH) return null;
  const share = Math.floor((widthBesideScrollbar * MAX_DOCKED_PANEL_PERCENT) / 100);
  return { min: MIN_PANEL_WIDTH, max: Math.max(MIN_PANEL_WIDTH, Math.min(room, share)) };
};

export const clampDockedPanelWidth = (width: number, { min, max }: DockedPanelWidthLimits) => (
  Math.round(Math.min(Math.max(width, min), max))
);

// The chosen width after the member asks for `requested`. While the room beside the page
// narrows the panel below the chosen width, asking for the widest it can be right now
// (or wider) keeps the chosen width, so it still comes back once the room does. Asking
// for less than that is a new choice.
export const nextChosenDockedPanelWidth = (
  requested: number,
  chosen: number,
  limits: DockedPanelWidthLimits,
) => {
  const next = clampDockedPanelWidth(requested, limits);
  return next === limits.max ? Math.max(chosen, next) : next;
};

// The member's chosen width, narrowed to fit the room beside the page so the page keeps
// its minimum width. The chosen width comes back once the room does.
const dockedPanelWidth = (
  widthBesideScrollbar: number,
  sidebarWidth: number,
  chosenWidth: number = DOCKED_PANEL_WIDTH,
): number | null => {
  const limits = dockedPanelWidthLimits(widthBesideScrollbar, sidebarWidth);
  return limits && clampDockedPanelWidth(chosenWidth, limits);
};

export default dockedPanelWidth;
